// Resolves parsed free-text rows against the entity catalog into a
// matched/unmatched split for the human-triage confirmation screen.
//
// SELF-CONTAINED port of the private `resolveRows` helper from the example
// app's modules/entities/importer.ts. `importFromExcelFile` is intentionally
// NOT ported — this app has its own parser (src/support/dataImport/parseDataset.ts).

import { matchLabel } from "./match";
import type { EntityItemId, EntityMatchConfidence } from "./match";

// Inlined from the example app's modules/shared/types.ts.
export type MatchedEntity = {
  itemId: EntityItemId;
  confidence: EntityMatchConfidence;
  rawLabel: string;
};

export type UnmatchedEntityRow = {
  rawLabel: string;
  rawCategory?: string;
  suggestions: Array<{ itemId: EntityItemId; score: number }>;
};

export function resolveRows(
  rows: Array<{ label: string; rawCategory?: string }>,
): { matched: MatchedEntity[]; unmatched: UnmatchedEntityRow[] } {
  const matched: MatchedEntity[] = [];
  const unmatched: UnmatchedEntityRow[] = [];
  // A catalog item can have more than one raw row resolve to it (e.g. a
  // parent label and a sub-item alias both present as separate source rows,
  // like "Meraki IOT" + "CNIOT") — keep only the first hit per item so a
  // confirmed selection is never duplicated in the review/store.
  const seenItemIds = new Set<string>();

  for (const row of rows) {
    // If the row carried an explicit category (flat-table format), prefer
    // candidates whose catalog category matches it — but never hard-require
    // it; a mismatched/renamed category label shouldn't sink an otherwise
    // exact item match.
    const candidates = matchLabel(row.label);
    if (candidates.length === 0) {
      unmatched.push({ rawLabel: row.label, rawCategory: row.rawCategory, suggestions: [] });
      continue;
    }
    const top = candidates[0];
    if (top.confidence === "exact" || top.confidence === "alias") {
      if (seenItemIds.has(top.itemId)) continue;
      seenItemIds.add(top.itemId);
      matched.push({ itemId: top.itemId, confidence: top.confidence, rawLabel: row.label });
      continue;
    }
    // Fuzzy-only: never silently trust it — surface as unmatched with
    // ranked suggestions for the human-triage confirmation screen.
    unmatched.push({
      rawLabel: row.label,
      rawCategory: row.rawCategory,
      suggestions: candidates.map((c) => ({ itemId: c.itemId, score: c.score })),
    });
  }

  return { matched, unmatched };
}
